import { inject } from 'vue'
import { trackPageView } from './tracker'

export const pageViewTrackingKey = Symbol('page-view-tracking')

function contentKey(route) {
  const params = route.params || {}
  const name = String(route.name || '')
  if (name.startsWith('film-list-') || name.startsWith('show-list-') ||
      name.startsWith('episode-list-') || name.startsWith('person-list-')) {
    return `${name.split('-')[0]}-list:${params.listSlug}`
  }
  if (name === 'film-detail' || name === 'film-section') return `film:${params.slug}`
  if (name === 'show-detail' || name === 'show-section') return `show:${params.slug}`
  if (name === 'person-detail' || name === 'person-section') return `person:${params.slug}`
  if (name === 'season-detail') return `season:${params.slug}:${params.seasonNumber}`
  if (name === 'episode-detail') {
    return `episode:${params.showSlug}:${params.seasonNumber}:${params.episodeNumber}`
  }
  if (['home', 'home-films', 'home-content', 'public-profile', 'demo-profile'].includes(name)) {
    return `profile:${route.fullPath}`
  }
  return null
}

export function createPageViewTracker({ auth = null, send = trackPageView, initialStatus = 200 } = {}) {
  let pending = null
  let loadedContent = null
  let firstNavigation = true

  function confirm(navigation) {
    if (!navigation || pending !== navigation || navigation.sent || navigation.excluded) return false
    navigation.sent = true
    loadedContent = navigation.content
    return send(navigation.to, navigation.from, auth)
  }

  return {
    navigate(to, from, failure) {
      if (failure) return
      const automaticLogin = to.name === 'login' && (
        to.redirectedFrom || to.query?.auth_redirect === '1'
      )
      pending = {
        to, from, sent: false, content: contentKey(to),
        excluded: to.name === 'not-found' || automaticLogin ||
          (firstNavigation && Number(initialStatus) >= 400),
      }
      firstNavigation = false
      // Anonymous You sections render a not-found view despite a cached entity.
      const cachedContent = pending.content === loadedContent && (
        to.params?.section !== 'you' || auth?.isAuthenticated?.value
      )
      if (!pending.content || cachedContent) confirm(pending)
    },
    capture() {
      const navigation = pending
      return () => confirm(navigation)
    },
  }
}

// A request captures its own navigation so a late response cannot count a new page.
export function usePageViewTracking() {
  return inject(pageViewTrackingKey, () => () => false)
}
